import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useCheckout } from '../hooks/useBilling';
import { PLAN_PRICING } from '../utils/planConfig';

const plans = [
  {
    id: 'free' as const,
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'For small teams getting started',
    features: ['Up to 2 users', '1 project', '1 client', 'Core task management'],
    cta: 'Get started',
    highlight: false,
  },
  {
    id: 'starter' as const,
    name: 'Starter',
    price: `$${PLAN_PRICING.starter.price}`,
    period: 'per user/mo',
    description: 'For growing agencies',
    features: ['Unlimited users (billed per seat)', 'Unlimited projects & clients', 'File uploads', 'Subtasks & comments'],
    cta: 'Upgrade to Starter',
    highlight: false,
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: `$${PLAN_PRICING.pro.price}`,
    period: 'per user/mo',
    description: 'For teams that need insights',
    features: ['Everything in Starter', 'Analytics dashboard', 'Quoted-value reporting', 'Role management'],
    cta: 'Upgrade to Pro',
    highlight: true,
  },
];

const PricingSection: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const highlightGlow = useColorModeValue('blue.50', 'whiteAlpha.100');
  const { user } = useAuth();
  const checkout = useCheckout();

  const handleSelect = (planId: 'free' | 'starter' | 'pro') => {
    if (planId === 'free') {
      window.location.href = user ? '/dashboard' : '/auth';
      return;
    }
    if (!user) {
      window.location.href = '/auth';
      return;
    }
    checkout.mutate({ plan: planId });
  };

  return (
    <Box id="pricing" py={{ base: 16, md: 20 }} scrollMarginTop="80px">
      <VStack spacing={4} textAlign="center" mb={14}>
        <Badge colorScheme="blue" px={3} py={1} borderRadius="full" textTransform="none">
          Pricing
        </Badge>
        <Heading size="2xl">Simple, transparent pricing</Heading>
        <Text color={muted} fontSize="lg" maxW="2xl">
          Start free with a 14-day Pro trial. Upgrade when your team needs more seats, storage, or analytics.
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} alignItems="stretch">
        {plans.map((plan) => (
          <Box
            key={plan.id}
            bg={plan.highlight ? highlightGlow : cardBg}
            p={8}
            borderRadius="2xl"
            boxShadow={plan.highlight ? '2xl' : 'lg'}
            borderWidth={plan.highlight ? 2 : 1}
            borderColor={plan.highlight ? 'blue.400' : borderColor}
            position="relative"
            transform={plan.highlight ? { lg: 'scale(1.03)' } : undefined}
            transition="transform 0.2s ease"
            _hover={{ transform: plan.highlight ? { lg: 'scale(1.05)' } : 'translateY(-4px)' }}
          >
            {plan.highlight && (
              <Badge position="absolute" top={-3} left="50%" transform="translateX(-50%)" colorScheme="blue" px={3}>
                Most popular
              </Badge>
            )}
            <VStack align="start" spacing={5} h="full">
              <Box>
                <Heading size="md" mb={1}>{plan.name}</Heading>
                <Text color={muted} fontSize="sm">{plan.description}</Text>
              </Box>

              <HStack align="baseline" spacing={2}>
                <Text fontSize="5xl" fontWeight="extrabold" lineHeight="1">
                  {plan.price}
                </Text>
                <Text color={muted} fontSize="sm">{plan.period}</Text>
              </HStack>

              <List spacing={3} w="full" flex="1">
                {plan.features.map((f) => (
                  <ListItem key={f} fontSize="sm" color={muted}>
                    <ListIcon as={FaCheck} color="green.500" />
                    {f}
                  </ListItem>
                ))}
              </List>

              <Button
                w="full"
                size="lg"
                colorScheme={plan.highlight ? 'blue' : 'gray'}
                variant={plan.highlight ? 'solid' : 'outline'}
                bgGradient={plan.highlight ? 'linear(to-r, blue.500, purple.500)' : undefined}
                _hover={
                  plan.highlight
                    ? { bgGradient: 'linear(to-r, blue.600, purple.600)' }
                    : { bg: 'gray.50' }
                }
                onClick={() => handleSelect(plan.id)}
                isLoading={checkout.isPending && plan.id !== 'free'}
              >
                {plan.cta}
              </Button>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      <Text textAlign="center" mt={12} fontSize="sm" color={muted}>
        Enterprise plans available on request.{' '}
        <Button as={RouterLink} to="/terms" variant="link" size="sm">Terms</Button>
        {' · '}
        <Button as={RouterLink} to="/privacy" variant="link" size="sm">Privacy</Button>
      </Text>
    </Box>
  );
};

export default PricingSection;
